import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

interface WebRTCPlayerProps {
  video_server: string;
  stream_id: string;
  channel_id: string;
  width?: string | number; // Width can be a number (for pixels) or a string (for percentages, etc.)
  height?: string | number; // Height can be a number (for pixels) or a string (for percentages, etc.)
  authToken: string; // Add in base 64 encoded authentication
}

const WebRTCPlayer: React.FC<WebRTCPlayerProps> = ({
  width,
  height,
  stream_id,
  channel_id,
  video_server,
  authToken,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const src = `${video_server}/stream/${stream_id}/channel/${channel_id}/webrtc?uuid=${stream_id}&channel=${channel_id}`;
  const [errorMessage, setErrorMessage] = useState<string>("");
  
  console.log("=== WEBRTCPLAYER: Initializing ===");
  console.log("video_server:", video_server);
  console.log("stream_id:", stream_id);
  console.log("channel_id:", channel_id);
  console.log("authToken:", authToken);
  console.log("Connection URL:", src);
  
  useEffect(() => {
    const abortController = new AbortController();
    let pc: RTCPeerConnection | null = null;

    // Global handler for unhandled promise rejections in this component
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.name === 'AbortError') {
        console.log('Suppressed AbortError from WebRTC component');
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    const startPlay = async (videoEl: HTMLVideoElement, url: string) => {
      try {
        pc = new RTCPeerConnection({
          iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
        });
    
        pc.ontrack = (event) => {
          videoEl.srcObject = event.streams[0];
          videoEl.play().catch((err) => {
            if (err.name === 'AbortError' || err.name === 'NotAllowedError') {
              console.log('Video play cancelled or not allowed:', err.name);
            } else {
              console.error('Error playing video:', err);
            }
          });
        };
    
        pc.addTransceiver("video", { direction: "sendrecv" });
    
        pc.onnegotiationneeded = async () => {
          try {
            const offer = await pc!.createOffer();
            await pc!.setLocalDescription(offer);
    
            const response = await fetch(url, {
              method: "POST",
              headers: {
                Authorization: `Basic ${authToken}`, // e.g. "Basic aW5maW5pd2VsbDp4YjI1aWYyMDI0IQ=="
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({
                data: btoa(pc!.localDescription!.sdp),
              }),
              signal: abortController.signal,
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const answerBase64 = await response.text();
            if (pc && pc.signalingState !== 'closed') {
              await pc.setRemoteDescription(
                new RTCSessionDescription({ type: "answer", sdp: atob(answerBase64) })
              );
            }
          } catch (err: any) {
            if (err.name === 'AbortError') {
              console.log('WebRTC request cancelled (component unmounted)');
              return;
            }
            console.error("Error negotiating with server:", err);
            setErrorMessage("Unable to connect to video server");
          }
        };
    
        // Optional data channel, for demonstration
        const webrtcSendChannel = pc.createDataChannel("rtsptowebSendChannel");
        webrtcSendChannel.onopen = () => {
          console.log(`${webrtcSendChannel.label} has opened`);
          webrtcSendChannel.send("ping");
        };
        webrtcSendChannel.onclose = () => {
          console.log(`${webrtcSendChannel.label} has closed`);
          if (!abortController.signal.aborted && videoEl) {
            startPlay(videoEl, url);
          }
        };
        webrtcSendChannel.onmessage = (event) => console.log(event.data);
      } catch (err: any) {
        if (err.name === 'AbortError') {
          console.log('WebRTC initialization cancelled');
          return;
        }
        console.error("Error initializing WebRTC:", err);
        setErrorMessage("Failed to initialize video player");
      }
    };
  
    if (videoRef.current) {
      startPlay(videoRef.current, src);
    }

    // Cleanup function
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      abortController.abort();
      if (pc) {
        pc.close();
      }
    };
  }, [src, authToken, video_server, stream_id, channel_id]);
  
  // Prepare the video styles
  const videoStyles = {
    maxWidth: width ? width : "100%", // Use provided width or fallback to 100%
    maxHeight: height ? height : "100%", // Use provided height or fallback to 100%
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {errorMessage ? (
        <Box 
          sx={{ 
            padding: 2, 
            textAlign: 'center',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {errorMessage}
          </Typography>
        </Box>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          controls
          style={videoStyles}
        ></video>
      )}
    </Box>
  );
};

export default WebRTCPlayer;